import { clamp } from "lodash-es";

import { refreshTracks } from "../main-canvas/messages/post-track-messages.ts";
import { getAllTracksHeight } from "../main-canvas/utils.ts";
import { useGenomeViewerStore } from "../store/store.ts";
import { SHAPE_HEIGHT_WITHOUT_TEXT } from "../track-workers/graphics/settings.ts";
import { type NavigationFeature, type TrackGroup } from "../types.ts";
import { align } from "./align.ts";
import { getTrackOffsetY } from "./graphics/track-offset.ts";
import { getTrackTypeBySyntenyType, getTrackTypeFromGenomicFeatureType } from "./type-conversion.ts";

export const navigateToFeature = (item: NavigationFeature, forceAlign = false) => {
  const { activeSyntenyType, ctx, trackGroups, tracks, basePerPixel, orthologousGroups } =
    useGenomeViewerStore.getState();

  let trackGroup: TrackGroup | undefined;
  const trackGroupMatchingContigAndOrg = [...trackGroups.values()].find(
    (group) => group.contigId === item.contigId && group.organismId === item.orgId,
  );

  if (!trackGroupMatchingContigAndOrg) {
    const trackGroupMatchingOrg = [...trackGroups.values()].find((group) => group.organismId === item.orgId);

    if (trackGroupMatchingOrg) {
      // Contig isn't found so we perform a contig swap
      trackGroup = { ...trackGroupMatchingOrg, graphics: { ...trackGroupMatchingOrg.graphics, isVisible: true } };
      trackGroups.set(trackGroup.groupId, trackGroup);
      useGenomeViewerStore.getState().updateTrackContigId(trackGroup.groupId, item.contigId);
      refreshTracks();
    }
  } else {
    trackGroup = {
      // Contig is already existing, so we ensure the trackGroup is visible
      ...trackGroupMatchingContigAndOrg,
      graphics: { ...trackGroupMatchingContigAndOrg.graphics, isVisible: true },
    };
    trackGroups.set(trackGroup.groupId, trackGroup);
  }

  // Impossible to navigate to the specified feature in the current synteny set
  if (!trackGroup || !item.id || !item.type || !item.start || !item.stop) {
    return;
  }

  const trackType = getTrackTypeFromGenomicFeatureType(item.type);

  const trackId = trackGroup.trackIds.find((id) => {
    const track = tracks.get(id);
    if (!track) return false;

    const isTypeMatch = track.trackType === trackType;
    const isSubTypeMatch = item.subTypeId === undefined || track.subType?.id == item.subTypeId;

    return isTypeMatch && isSubTypeMatch;
  });

  if (!trackId) return;

  if (!tracks.get(trackId)!.isVisible) {
    tracks.set(trackId, { ...tracks.get(trackId)!, isVisible: true });
  }

  const itemWidth = (item.stop - item.start) / basePerPixel;
  const scale = (2 * itemWidth) / ctx!.canvas.width;

  const totalHeight = getAllTracksHeight();
  const newScrollX =
    (trackGroup.graphics.baseShift + item.start) / basePerPixel / scale - ctx!.canvas.width / 2 + itemWidth / 2 / scale;

  const newScrollY = clamp(
    getTrackOffsetY(trackGroups, tracks, trackId) - ctx!.canvas.height / 2 + SHAPE_HEIGHT_WITHOUT_TEXT / 2,
    0,
    totalHeight - ctx!.canvas.height,
  );

  useGenomeViewerStore.setState({
    scrollX: newScrollX,
    scrollY: newScrollY,
    basePerPixel: basePerPixel * scale,
    tracks,
    trackGroups,
    activeMemberId: forceAlign ? item.id : undefined,
  });

  if (forceAlign) {
    const familyId = orthologousGroups.find((family) =>
      family.orthologs.some((ortholog) => ortholog.id === item.id),
    )?.orthologId;
    if (familyId && tracks.get(trackId)?.trackType === getTrackTypeBySyntenyType(activeSyntenyType!)) {
      align(
        {
          featureId: item.id,
          trackId,
          trackGroupId: trackGroup.groupId,
          contigId: trackGroup.contigId,
          familyId: familyId,
          start: item.start,
        },
        {
          forceCenter: true,
        },
      );
    }
  }

  tracks.get(trackId)?.worker.postMessage({
    type: "highlight",
    highlightFeatureId: item.id,
    requiresHighlight: true,
  });
  refreshTracks();
};
